import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useWallet } from '../../hooks/useWallet';
import { CHAIN_CONFIGS } from '../../utils/constants';

// Contract addresses for Shido and Ethereum bridges
const BRIDGE_CONTRACTS = {
  'shido-mainnet': CHAIN_CONFIGS['shido-mainnet']?.bridge,
  'ethereum': CHAIN_CONFIGS['ethereum']?.bridge,
};

// Minimal ABI for owner and pause functions
const BRIDGE_ABI = [
  'function owner() view returns (address)',
  'function paused() view returns (bool)',
  'function configPause(bool _paused) external',
];

export const ControlPage: React.FC = () => {
  const { walletInfo, connectWallet, isConnecting } = useWallet();
  
  const [isPaused, setIsPaused] = useState<boolean | null>(null);
  const [isOwner, setIsOwner] = useState<boolean>(false);
  const [ownerAddress, setOwnerAddress] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedChain, setSelectedChain] = useState<'shido-mainnet' | 'ethereum'>('shido-mainnet');

  // Get the bridge address for the selected chain
  const bridgeAddress = BRIDGE_CONTRACTS[selectedChain];

  // Fetch contract state
  const fetchContractState = useCallback(async () => {
    if (!bridgeAddress) {
      setError('Bridge address not configured for this chain');
      return;
    }

    try {
      const provider = new ethers.providers.JsonRpcProvider(
        selectedChain === 'shido-mainnet' 
          ? 'https://evm.shidoscan.net'
          : 'https://eth.llamarpc.com'
      );
      
      const contract = new ethers.Contract(bridgeAddress, BRIDGE_ABI, provider);
      
      // Fetch owner and paused state
      const [owner, paused] = await Promise.all([
        contract.owner(),
        contract.paused(),
      ]);
      
      setOwnerAddress(owner);
      setIsPaused(paused);
      
      // Check if connected wallet is owner
      if (walletInfo.address) {
        setIsOwner(owner.toLowerCase() === walletInfo.address.toLowerCase());
      }
    } catch (err) {
      console.error('Error fetching contract state:', err);
      setError('Failed to fetch contract state');
    }
  }, [bridgeAddress, walletInfo.address, selectedChain]);

  // Fetch state on mount and when chain/wallet changes
  useEffect(() => {
    fetchContractState();
  }, [fetchContractState]);

  // Toggle pause state
  const handleTogglePause = async () => {
    if (!walletInfo.isConnected || !isOwner || !bridgeAddress) return;
    
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Get signer from window.ethereum
      if (!window.ethereum) {
        throw new Error('No wallet connected');
      }
      
      const provider = new ethers.providers.Web3Provider(window.ethereum as any);
      const signer = provider.getSigner();
      
      const contract = new ethers.Contract(bridgeAddress, BRIDGE_ABI, signer);
      
      const newPauseState = !isPaused;
      const tx = await contract.configPause(newPauseState);
      
      setSuccess(`Transaction submitted: ${tx.hash}`);
      
      // Wait for confirmation
      await tx.wait();
      
      setSuccess(`Bridge ${newPauseState ? 'paused' : 'resumed'} successfully!`);
      setIsPaused(newPauseState);
    } catch (err: any) {
      console.error('Error toggling pause:', err);
      setError(err.message || 'Failed to toggle pause state');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative max-w-2xl mx-auto z-10">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-secondary-500 mb-2">
          Bridge Control Panel
        </h1>
        <p className="text-sm sm:text-base text-gray-400">
          Admin controls for the Shido Native Bridge
        </p>
      </div>

      <Card elevated>
        <div className="space-y-6">
          {/* Chain Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Select Chain</label>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedChain('shido-mainnet')}
                className={`flex-1 px-4 py-2 rounded-lg border transition-all ${
                  selectedChain === 'shido-mainnet'
                    ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                    : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600'
                }`}
              >
                Shido Network
              </button>
              <button
                onClick={() => setSelectedChain('ethereum')}
                className={`flex-1 px-4 py-2 rounded-lg border transition-all ${
                  selectedChain === 'ethereum'
                    ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                    : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600'
                }`}
              >
                Ethereum
              </button>
            </div>
          </div>

          {/* Contract Info */}
          <div className="bg-gray-800/30 rounded-lg p-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-400">Contract Address:</span>
              <span className="text-sm text-gray-300 font-mono">
                {bridgeAddress ? `${bridgeAddress.slice(0, 8)}...${bridgeAddress.slice(-6)}` : 'Not configured'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-400">Owner:</span>
              <span className="text-sm text-gray-300 font-mono">
                {ownerAddress ? `${ownerAddress.slice(0, 8)}...${ownerAddress.slice(-6)}` : 'Loading...'}
              </span>
            </div>
          </div>

          {/* Wallet Connection */}
          {!walletInfo.isConnected ? (
            <div className="text-center py-8">
              <p className="text-gray-400 mb-4">Connect your wallet to access admin controls</p>
              <Button onClick={connectWallet} isLoading={isConnecting} glow>
                Connect Wallet
              </Button>
            </div>
          ) : !isOwner ? (
            <div className="bg-red-900/20 border border-red-800 rounded-lg p-6 text-center">
              <div className="text-4xl mb-3">🚫</div>
              <h3 className="text-lg font-semibold text-red-400 mb-2">Access Denied</h3>
              <p className="text-sm text-red-400/80">
                Connected wallet is not the contract owner
              </p>
              <p className="text-xs text-gray-500 mt-2 font-mono">
                Your address: {walletInfo.address?.slice(0, 8)}...{walletInfo.address?.slice(-6)}
              </p>
            </div>
          ) : (
            <>
              {/* Pause Status */}
              <div className="bg-gray-800/30 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">Bridge Status</h3>
                    <p className="text-sm text-gray-400">
                      {isPaused === null 
                        ? 'Loading...' 
                        : isPaused 
                          ? 'Bridge is currently paused. Users cannot bridge tokens.'
                          : 'Bridge is active. Users can bridge tokens normally.'
                      }
                    </p>
                  </div>
                  <div className={`
                    flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium
                    ${isPaused === null 
                      ? 'bg-gray-700 text-gray-400' 
                      : isPaused 
                        ? 'bg-red-900/30 text-red-400' 
                        : 'bg-emerald-900/30 text-emerald-400'
                    }
                  `}>
                    <span className={`w-2.5 h-2.5 rounded-full ${
                      isPaused === null 
                        ? 'bg-gray-500' 
                        : isPaused 
                          ? 'bg-red-400' 
                          : 'bg-emerald-400 animate-pulse'
                    }`} />
                    {isPaused === null ? 'Loading' : isPaused ? 'Paused' : 'Active'}
                  </div>
                </div>
              </div>

              {/* Pause/Unpause Button */}
              <Button
                onClick={handleTogglePause}
                disabled={isLoading || isPaused === null}
                isLoading={isLoading}
                fullWidth
                variant={isPaused ? 'primary' : 'danger'}
                className="py-4 text-lg"
              >
                {isPaused ? '▶️ Resume Bridge' : '⏸️ Pause Bridge'}
              </Button>

              {/* Warning */}
              {!isPaused && (
                <div className="bg-amber-900/20 border border-amber-700 rounded-lg p-3 flex items-start gap-2">
                  <span className="text-amber-400">⚠️</span>
                  <p className="text-sm text-amber-400/80">
                    Pausing the bridge will prevent all cross-chain transfers. Only pause in case of emergency.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-900/30 border border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-900/30 border border-green-800 rounded-lg p-3">
              <p className="text-sm text-green-400">{success}</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
